"use client";

import { useState, useRef, useEffect } from "react";
import { Label } from "../ui/label";
import { Upload } from "lucide-react";
import { AddPayments, ImageProcessing } from "../../Api";
import { useAuthChecker } from "../../Context";
import { Drawer, Form, Image, message, Switch } from "antd";
import { Button } from "../ui/button";
import { IMAGE_URL, PaymentType, PaymentTypeEnum } from "../../Constant";
import SlipForm from "../SlipForm/SlipForm";
import NeuralNetworkBG from "../NeuralNetworkBG/NeuralNetworkBG";
import { PaymentMetaData } from "../PaymentMetaData/PaymentMetaData";
import {
  copyToClipboard,
  handleDragOver,
  handleDrop,
  handleImageUpload,
} from "../../lib";
import { PurchaseOrderForm } from "../PurchaseOrderForm/PurchaseOrderForm";
import { MultiInquirySelector } from "../MultiInquirySelector/MultiInquirySelector";
import { MultiVehicleSelector } from "../MultiVehicleSelector/MultiVehicleSelector";
import { BulkPaymentForm } from "../BulkPaymentForm/BulkPaymentForm";
import { FetchDealers_api } from "../../Api";
import { Select } from "antd";

const { Option } = Select;

export const PaymentProcess = () => {
  const user = useAuthChecker();
  const [slipForm] = Form.useForm();
  const [poForm] = Form.useForm();
  const [remittanceForm] = Form.useForm();

  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [result, setResult] = useState<null | { message: string }>(null);
  const [progress, setProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [mode, setMode] = useState<"process" | "manual">("process");
  const [type, setType] = useState<PaymentType>(PaymentTypeEnum.SLIP);
  const [openFormDrawer, setOpenFormDrawer] = useState(false);
  const [totalAmount, setTotalAmount] = useState<number>(0);
  const [addedInquiyData, setAddedInquiyData] = useState<any>([]);
  const [addedCustomerData, setAddedCustomeData] = useState<any>([]);
  const [addedPaymentTypeData, setAddedPaymentTypeData] = useState<any>([]);
  const [selectedInquiries, setSelectedInquiries] = useState<any>([]);
  const [selectedVehicles, setSelectedVehicles] = useState<any>([]);
  const [dealers, setDealers] = useState<any>([]);
  const [selectedDealerId, setSelectedDealerId] = useState<number | null>(null);

  const [isAdding, setIsAdding] = useState(false);
  const [imageName, setImageName] = useState("");
  const [imageMetaData, setImageMetaData] = useState<File>();

  const handleFile = async (file: File) => {
    setImageMetaData(file);
    setIsAnalyzing(true);
    setProgress(0);
    setIsLoading(true);

    try {
      const response = await ImageProcessing(file, type);
      const text = response?.result?.candidates?.[0]?.content?.parts?.[0]?.text;
      if (text) {
        const cleanedText = text.replace(/```json\s*|```/g, "").trim();
        try {
          const resObj = JSON.parse(cleanedText);

          if (type === PaymentTypeEnum.SLIP) {
            slipForm.setFieldsValue(resObj);
          } else if (type === PaymentTypeEnum.PO) {
            poForm.setFieldsValue(resObj);
          } else if (type === PaymentTypeEnum.REMITTANCE) {
            remittanceForm.setFieldsValue(resObj);
          }
          setImageName(response?.imageName);
        } catch (err) {
          console.error("Failed to parse JSON:", err);
        }
      }
    } catch (error) {
      console.error("Image processing failed:", error);
    } finally {
      let progressVal = 0;
      const interval = setInterval(() => {
        progressVal += 10;
        setProgress(progressVal);
        if (progressVal >= 100) {
          clearInterval(interval);
          setTimeout(() => {
            setIsAnalyzing(false);
            setResult({ message: "✅ Image analyzed successfully!" });
          }, 500);
        }
      }, 150);
      setIsLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (
      type != PaymentTypeEnum.REMITTANCE &&
      (!addedCustomerData?.id ||
        !addedPaymentTypeData?.id ||
        !addedInquiyData?.id)
    ) {
      message.warning(
        "Please add customer, payment, and inquiry details before submitting.",
      );
      return;
    } else if (
      type === PaymentTypeEnum.REMITTANCE &&
      selectedInquiries.length == 0
    ) {
      message.warning("Please select at least one inquiry before submitting.");
      return;
    }

    try {
      setIsAdding(true);
      const payload = {
        formData:
          type == PaymentTypeEnum.SLIP
            ? slipForm.getFieldsValue()
            : type === PaymentTypeEnum.PO
              ? poForm.getFieldsValue()
              : remittanceForm.getFieldsValue(),
        customer: addedCustomerData?.id,
        paymentType: addedPaymentTypeData?.id,
        inquiry: addedInquiyData?.id,
        userId: user?.user?.userId,
        imageName,
        selectedInquiries: selectedInquiries,
        selectedVehicles: selectedVehicles,
        dealerId: selectedDealerId,
        type,
      };

      const res = await AddPayments(payload);
      if (res?.isDataAdded) {
        handleReset();
        message.success("Data added");
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsAdding(false);
      setOpenFormDrawer(false);
    }
  };

  const handleReset = () => {
    setResult(null);
    setIsAnalyzing(false);

    if (type === PaymentTypeEnum.SLIP) {
      slipForm.resetFields();
    } else if (type === PaymentTypeEnum.PO) {
      poForm.resetFields();
    } else {
      remittanceForm.resetFields();
    }
    setImageName("");
    setAddedInquiyData([]);
    setAddedCustomeData([]);
    setAddedPaymentTypeData([]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleClose = () => setOpenFormDrawer(false);

  useEffect(() => {
    if (result || mode === "manual") {
      setOpenFormDrawer(true);
    }
  }, [result, mode]);

  useEffect(() => {
    openFormDrawer ? setMode("manual") : setMode("process");
  }, [openFormDrawer]);

  const filterdCdataWithInqId = addedCustomerData?.inquiries?.find(
    (inquiry) => String(inquiry.id) === String(addedInquiyData?.id),
  );

  return (
    <div className="flex flex-col items-center justify-center min-h-screen p-6 space-y-8">
      <NeuralNetworkBG />

      <h1 className="text-3xl font-bold text-gray-800 tracking-tight">
        Document Processor
      </h1>

      <div className="flex gap-4 bg-white border border-gray-200 rounded-xl shadow-sm p-2">
        <Button
          disabled={isLoading || openFormDrawer}
          className={`transition-all duration-200 ${
            type === PaymentTypeEnum.SLIP
              ? "bg-blue-600 text-white shadow-md"
              : "bg-white text-gray-700 hover:bg-blue-50"
          } px-6 py-2 rounded-lg font-medium`}
          onClick={() => setType(PaymentTypeEnum.SLIP)}
        >
          Slip
        </Button>
        <Button
          disabled={isLoading || openFormDrawer}
          className={`transition-all duration-200 ${
            type === PaymentTypeEnum.PO
              ? "bg-blue-600 text-white shadow-md"
              : "bg-white text-gray-700 hover:bg-blue-50"
          } px-6 py-2 rounded-lg font-medium`}
          onClick={() => setType(PaymentTypeEnum.PO)}
        >
          PO
        </Button>
        <Button
          disabled={isLoading || openFormDrawer}
          className={`transition-all duration-200 ${
            type === PaymentTypeEnum.REMITTANCE
              ? "bg-blue-600 text-white shadow-md"
              : "bg-white text-gray-700 hover:bg-blue-50"
          } px-6 py-2 rounded-lg font-medium`}
          onClick={() => setType(PaymentTypeEnum.REMITTANCE)}
        >
          Remittance
        </Button>
      </div>

      <div className="flex items-center space-x-4 bg-white border border-gray-200 rounded-xl shadow-sm px-5 py-3 transition-all duration-200 hover:shadow-md">
        <Label className="text-sm text-gray-700 font-medium">
          {mode === "manual" ? "Manual Mode" : "Process Mode"}
        </Label>
        <Switch
          disabled={isLoading || openFormDrawer}
          checked={mode === "manual"}
          onChange={(checked) => setMode(checked ? "manual" : "process")}
          className="data-[state=checked]:bg-blue-600 data-[state=unchecked]:bg-gray-300"
        />
      </div>

      <div
        className="w-full max-w-md h-56 border-2 border-dashed border-gray-300 bg-white rounded-2xl flex flex-col items-center justify-center text-center p-6 cursor-pointer transition-all duration-300 hover:border-blue-400 hover:shadow-lg"
        onDrop={(e) => {
          handleDrop(e, handleFile);
        }}
        onDragOver={(e) => {
          handleDragOver(e);
        }}
        onClick={() => fileInputRef.current?.click()}
      >
        <Upload className="w-10 h-10 mb-3 text-blue-500" />
        <p className="text-gray-600 font-medium">
          Drag & drop an image or PDF here
        </p>
        <p className="text-sm text-gray-400 mt-1">or click to browse</p>
        <input
          disabled={openFormDrawer || isAnalyzing}
          ref={fileInputRef}
          type="file"
          accept="image/*,.pdf"
          onChange={(e) => {
            handleImageUpload(e, handleFile);
          }}
          className="hidden"
        />
      </div>

      {/* Processing */}
      {isAnalyzing && (
        <div className="flex flex-col items-center space-y-4">
          <div className="relative">
            <div className="w-12 h-12 rounded-full border-4 border-blue-100 animate-spin border-t-blue-500"></div>
          </div>
          <p className="text-gray-600 font-medium">{progress}% Complete</p>
        </div>
      )}

      {/* Form */}
      <Drawer
        title={
          type === PaymentTypeEnum.SLIP
            ? "Add Slip"
            : type === PaymentTypeEnum.PO
              ? "Add PO"
              : type === PaymentTypeEnum.REMITTANCE
                ? "Add Remittance"
                : "Add Dealer Sale"
        }
        placement="right"
        width={750}
        onClose={handleClose}
        open={openFormDrawer}
        destroyOnClose
      >
        <div>
          {imageName && (
            <Image
              src={`${IMAGE_URL}/upload/${imageName}`}
              alt="Slip Preview"
              width={20}
              style={{ borderRadius: 8 }}
            >
              Click to see image
            </Image>
          )}
          {type === PaymentTypeEnum.SLIP || type === PaymentTypeEnum.PO ? (
            <>
              <PaymentMetaData
                setAddedInquiyData={setAddedInquiyData}
                setAddedCustomeData={setAddedCustomeData}
                setAddedPaymentTypeData={setAddedPaymentTypeData}
              />

              <div className="flex gap-x-5 mt-3">
                <div>
                  <p className="text-gray-500 text-xs">Total</p>
                  <p className="font-semibold text-blue-600">
                    Rs.{" "}
                    {(
                      Number(filterdCdataWithInqId?.totalamount) || 0
                    ).toLocaleString()}
                  </p>
                </div>

                <div>
                  <p className="text-gray-500 text-xs">Paid</p>
                  <p className="font-semibold text-green-600">
                    Rs.{" "}
                    {(
                      Number(filterdCdataWithInqId?.paidamount) || 0
                    ).toLocaleString()}
                  </p>
                </div>

                <div>
                  <p className="text-gray-500 text-xs">Due</p>
                  <p className="font-semibold text-red-600">
                    Rs.{" "}
                    {(
                      Number(filterdCdataWithInqId?.dueamount) || 0
                    ).toLocaleString()}
                  </p>
                </div>
              </div>
            </>
          ) : type === PaymentTypeEnum.REMITTANCE ? (
            <MultiInquirySelector
              totalAmount={totalAmount}
              setTotalAmount={setTotalAmount}
              selectedInquiries={selectedInquiries}
              setSelectedInquiries={setSelectedInquiries}
            />
          ) : null}
          {type == PaymentTypeEnum.SLIP ? (
            <SlipForm
              maximumAmount={Number(filterdCdataWithInqId?.dueamount) || 0}
              formData={slipForm}
              isAdding={isAdding}
              handleSubmit={handleSubmit}
              handleReset={handleReset}
              copyToClipboard={(value) => {
                copyToClipboard(value);
              }}
              imageMetaData={imageMetaData}
              handleFile={handleFile}
              isLoading={isLoading}
            />
          ) : type === PaymentTypeEnum.PO ? (
            <PurchaseOrderForm
              maximumAmount={Number(filterdCdataWithInqId?.dueamount) || 0}
              formData={poForm}
              isAdding={isAdding}
              handleSubmit={handleSubmit}
              handleReset={handleReset}
              copyToClipboard={(value) => {
                copyToClipboard(value);
              }}
              imageMetaData={imageMetaData}
              handleFile={handleFile}
              isLoading={isLoading}
            />
          ) : type === PaymentTypeEnum.REMITTANCE ? (
            <BulkPaymentForm
              maximumAmount={totalAmount}
              formData={remittanceForm}
              isAdding={isAdding}
              isLoading={isLoading}
              handleSubmit={handleSubmit}
              handleReset={handleReset}
              handleFile={handleFile}
              copyToClipboard={(value) => {
                copyToClipboard(value);
              }}
              payment_type={type}
              imageMetaData={imageMetaData}
            />
          ) : null}
        </div>
      </Drawer>
    </div>
  );
};
